import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
    FaArchive, FaCheck, FaCircle, FaEyeSlash, FaPlus, FaSave, FaSearch, FaStickyNote,
    FaThumbtack, FaTimes, FaExclamationTriangle, FaSlidersH,
} from "react-icons/fa";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldDate, FieldRichText, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { onApiAction } from "../../../../_UI/onApiAction";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { STICKY_NOTES_CONSTANTS } from "../_MODULES/_API";
import "../_UI/_Styles/StickyNotes.css";

const SAVE_DELAY = 650;
const DRAFT_PREFIX = "notebook:sticky-note-draft:";

const COLORS = [
    { value: "CHARCOAL", label: "Grafito" },
    { value: "YELLOW", label: "Amarillo" },
    { value: "BLUE", label: "Azul" },
    { value: "GREEN", label: "Verde" },
    { value: "PINK", label: "Rosa" },
    { value: "PURPLE", label: "Violeta" },
];

const plain = (html = "") => DOMPurify.sanitize(String(html), { ALLOWED_TAGS: [] })
    .replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const noteLabel = (note) => {
    const title = String(note?.title ?? "").trim();
    if (title) return title;
    const body = plain(note?.content ?? "");
    return body ? body.slice(0, 70) : "Nota sin título";
};

const notePreview = (note) => {
    const body = plain(note?.content ?? "");
    if (!body) return "Empezá a escribir…";
    return body.slice(0, 150);
};

const onFormatUpdated = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("es-AR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
};

const normalizeDraft = (note = {}) => ({
    title: note.title ?? "",
    content: note.content ?? "",
    color: note.color ?? "CHARCOAL",
});

const draftHash = (value) => JSON.stringify({
    title: String(value?.title ?? ""),
    content: String(value?.content ?? ""),
    color: String(value?.color ?? "CHARCOAL"),
});

export const StickyNotesViewMain = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(searchParams.get("noteAdvanced") === "1");
    const [advanced, setAdvanced] = useState({ pinned: "ALL", color: "ALL", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(normalizeDraft());
    const [saveState, setSaveState] = useState("saved");
    const [creating, setCreating] = useState(false);
    const saveTimerRef = useRef(null);
    const selectedIdRef = useRef(null);
    const draftRef = useRef(draft);
    const savedHashRef = useRef(draftHash(draft));
    const savingRef = useRef(false);
    const queuedSaveRef = useRef(false);

    const Q = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const load = useCallback(() => Q.apiGet({ endpoint: STICKY_NOTES_CONSTANTS.LIST }), [Q]);

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const notes = Array.isArray(Q.useApiData) ? Q.useApiData : [];
    const selected = useMemo(() => notes.find((item) => String(item.id) === String(selectedId)) ?? null, [notes, selectedId]);

    const advancedCount = [
        advanced.pinned !== "ALL",
        advanced.color !== "ALL",
        advanced.dateRange?.[0] || advanced.dateRange?.[1],
        advanced.dateExact,
    ].filter(Boolean).length;

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return [...notes]
            .filter((note) => {
                if (query && ![noteLabel(note), plain(note.content)].join(" ").toLowerCase().includes(query)) return false;
                if (advanced.pinned === "YES" && !note.isPinned) return false;
                if (advanced.pinned === "NO" && note.isPinned) return false;
                if (advanced.color !== "ALL" && String(note.color || "CHARCOAL") !== advanced.color) return false;
                const dateValue = advanced.dateField === "CREATED" ? note.createdAt : note.updatedAt;
                return onDateMatches(dateValue, advanced.dateRange, advanced.dateExact);
            })
            .sort((a, b) => {
                if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
                return String(b.updatedAt ?? b.createdAt ?? "").localeCompare(String(a.updatedAt ?? a.createdAt ?? ""));
            });
    }, [notes, search, advanced]);

    const writeLocalDraft = useCallback((id, value) => {
        if (!id) return;
        try {
            localStorage.setItem(`${DRAFT_PREFIX}${id}`, JSON.stringify({ ...value, savedAt: Date.now() }));
        } catch (_) {}
    }, []);

    const clearLocalDraft = useCallback((id) => {
        if (!id) return;
        try { localStorage.removeItem(`${DRAFT_PREFIX}${id}`); } catch (_) {}
    }, []);

    const readLocalDraft = useCallback((id) => {
        if (!id) return null;
        try {
            const raw = localStorage.getItem(`${DRAFT_PREFIX}${id}`);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch (_) { return null; }
    }, []);

    const saveCurrent = useCallback(async ({ force = false } = {}) => {
        const id = selectedIdRef.current;
        if (!id) return true;
        const current = draftRef.current;
        const currentHash = draftHash(current);
        if (!force && currentHash === savedHashRef.current) {
            setSaveState("saved");
            clearLocalDraft(id);
            return true;
        }
        if (savingRef.current) {
            queuedSaveRef.current = true;
            return false;
        }

        savingRef.current = true;
        queuedSaveRef.current = false;
        setSaveState("saving");
        const res = await onApiAction({
            method: "PATCH",
            endpoint: STICKY_NOTES_CONSTANTS.UPDATE,
            pathParams: { id },
            data: { note: current },
            isToastError: false,
        });
        savingRef.current = false;

        if (res.success && res.data) {
            const returnedHash = draftHash(res.data);
            if (String(selectedIdRef.current) === String(id) && draftHash(draftRef.current) === currentHash) {
                savedHashRef.current = returnedHash;
                setSaveState("saved");
                clearLocalDraft(id);
            }
            Q.mutateLocal({ id, patch: res.data });
        } else {
            setSaveState("error");
            writeLocalDraft(id, current);
        }

        if (queuedSaveRef.current && String(selectedIdRef.current) === String(id)) {
            queuedSaveRef.current = false;
            window.setTimeout(() => saveCurrent({ force: true }), 80);
        }
        return !!res.success;
    }, [Q, clearLocalDraft, writeLocalDraft]);

    const queueSave = useCallback((nextDraft) => {
        draftRef.current = nextDraft;
        setDraft(nextDraft);
        const id = selectedIdRef.current;
        if (!id) return;
        writeLocalDraft(id, nextDraft);
        setSaveState("dirty");
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = window.setTimeout(() => saveCurrent(), SAVE_DELAY);
    }, [saveCurrent, writeLocalDraft]);

    const selectNote = useCallback((note) => {
        if (!note) return;
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        if (selectedIdRef.current && draftHash(draftRef.current) !== savedHashRef.current) saveCurrent({ force: true });

        const local = readLocalDraft(note.id);
        const next = local ? normalizeDraft(local) : normalizeDraft(note);
        selectedIdRef.current = note.id;
        setSelectedId(note.id);
        draftRef.current = next;
        setDraft(next);
        savedHashRef.current = draftHash(normalizeDraft(note));
        setSaveState(local && draftHash(next) !== savedHashRef.current ? "dirty" : "saved");
        if (local && draftHash(next) !== savedHashRef.current) {
            saveTimerRef.current = window.setTimeout(() => saveCurrent({ force: true }), 180);
        }
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete("focus");
        nextParams.set("note", String(note.id));
        setSearchParams(nextParams, { replace: true });
    }, [readLocalDraft, saveCurrent, searchParams, setSearchParams]);

    useEffect(() => {
        if (!notes.length) {
            selectedIdRef.current = null;
            setSelectedId(null);
            return;
        }

        const focusId = searchParams.get("note") || searchParams.get("focus");
        if (focusId) {
            const target = notes.find((item) => String(item.id) === String(focusId));
            if (target && String(selectedIdRef.current) !== String(target.id)) selectNote(target);
            return;
        }

        if (selectedIdRef.current && !notes.some((item) => String(item.id) === String(selectedIdRef.current))) {
            selectedIdRef.current = null;
            setSelectedId(null);
        }
    }, [notes, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => {
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        if (selectedIdRef.current && draftHash(draftRef.current) !== savedHashRef.current) {
            writeLocalDraft(selectedIdRef.current, draftRef.current);
        }
    }, [writeLocalDraft]);

    useEffect(() => {
        setAdvancedOpen(searchParams.get("noteAdvanced") === "1");
    }, [searchParams]);

    const toggleAdvanced = () => {
        const nextOpen = !advancedOpen;
        setAdvancedOpen(nextOpen);
        const nextParams = new URLSearchParams(window.location.search);
        if (nextOpen) nextParams.set("noteAdvanced", "1");
        else nextParams.delete("noteAdvanced");
        setSearchParams(nextParams, { replace: true });
    };

    const clearAdvanced = () => setAdvanced({ pinned: "ALL", color: "ALL", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = previousOverflow; };
    }, []);

    const deselectNote = useCallback(async () => {
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        if (selectedIdRef.current && draftHash(draftRef.current) !== savedHashRef.current) {
            await saveCurrent({ force: true });
        }
        const emptyDraft = normalizeDraft();
        selectedIdRef.current = null;
        setSelectedId(null);
        draftRef.current = emptyDraft;
        setDraft(emptyDraft);
        savedHashRef.current = draftHash(emptyDraft);
        setSaveState("saved");
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete("note");
        nextParams.delete("focus");
        setSearchParams(nextParams, { replace: true });
    }, [saveCurrent, setSearchParams]);

    const closeWorkspace = useCallback(async () => {
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        if (selectedIdRef.current && draftHash(draftRef.current) !== savedHashRef.current) {
            await saveCurrent({ force: true });
        }
        navigate("/dashboard");
    }, [navigate, saveCurrent]);

    const createNote = async () => {
        if (creating) return;
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        if (selectedIdRef.current && draftHash(draftRef.current) !== savedHashRef.current) await saveCurrent({ force: true });
        setCreating(true);
        const res = await onApiAction({
            method: "POST", endpoint: STICKY_NOTES_CONSTANTS.CREATE,
            data: { note: { title: "", content: "", color: "CHARCOAL" } },
            successMessage: null,
        });
        setCreating(false);
        if (res.success && res.data) {
            Q.mutateLocal({ insert: res.data });
            selectedIdRef.current = null;
            selectNote(res.data);
        }
    };

    useEffect(() => {
        const onKeyDown = (event) => {
            if (!(event.ctrlKey || event.metaKey)) return;
            if (event.key.toLowerCase() === "s") {
                event.preventDefault();
                if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
                saveCurrent({ force: true });
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [saveCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePin = async () => {
        if (!selected) return;
        const nextValue = !selected.isPinned;
        Q.mutateLocal({ id: selected.id, patch: { isPinned: nextValue } });
        const res = await onApiAction({
            method: "PATCH", endpoint: STICKY_NOTES_CONSTANTS.UPDATE,
            pathParams: { id: selected.id }, data: { isPinned: nextValue }, isToastError: true,
        });
        if (!res.success) Q.mutateLocal({ id: selected.id, patch: { isPinned: selected.isPinned } });
        else if (res.data) Q.mutateLocal({ id: selected.id, patch: res.data });
    };

    const archiveSelected = async () => {
        if (!selected) return;
        if (!await confirm.danger(
            "Archivar nota rápida",
            `Se archiva “${noteLabel(selected)}”. Después podés recuperarla desde Archivados.`,
            { confirmText: "Archivar" },
        )) return;
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        if (draftHash(draftRef.current) !== savedHashRef.current) await saveCurrent({ force: true });
        const snapshot = selected;
        Q.mutateLocal({ id: selected.id, remove: true });
        clearLocalDraft(selected.id);
        selectedIdRef.current = null;
        setSelectedId(null);
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete("note");
        nextParams.delete("focus");
        setSearchParams(nextParams, { replace: true });
        const res = await onApiAction({
            method: "PATCH", endpoint: STICKY_NOTES_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: selected.id }, successMessage: "Nota archivada",
        });
        if (!res.success) Q.mutateLocal({ insert: snapshot });
    };

    const status = {
        saved: { icon: <FaCheck />, text: "Guardado" },
        saving: { icon: <FaCircle />, text: "Guardando…" },
        dirty: { icon: <FaCircle />, text: "Pendiente" },
        error: { icon: <FaExclamationTriangle />, text: "Guardado local · reintentá" },
    }[saveState] || { icon: <FaCheck />, text: "Guardado" };

    return (
        <div className="LStudio__sticky_modal_backdrop" role="presentation">
            <section className="LStudio__sticky_modal" role="dialog" aria-modal="true" aria-label="Notas rápidas">
                <header className="LStudio__sticky_modal_header">
                    <div className="LStudio__sticky_modal_identity">
                        <span><FaStickyNote /></span>
                        <div>
                            <strong>Notas rápidas</strong>
                            <small>Guardado automático</small>
                        </div>
                    </div>

                    <div className="LStudio__sticky_modal_actions">
                        <button type="button" className="LStudio__sticky_modal_close" aria-label="Cerrar Notas rápidas" title="Cerrar y volver a Trabajo · Panel" onClick={closeWorkspace}>
                            <FaTimes />
                        </button>
                    </div>
                </header>

                <div className="LStudio__sticky_workspace">
                    <aside className="LStudio__sticky_list_panel">
                        <div className="LStudio__sticky_filterbar">
                            <div className="LStudio__sticky_search">
                                <FaSearch />
                                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar notas..." />
                                {search && <button type="button" title="Limpiar búsqueda" onClick={() => setSearch("")}><FaTimes /></button>}
                            </div>
                            <button type="button" className="LStudio__sticky_filter_toggle" data-open={advancedOpen} title="Filtros avanzados" aria-label="Filtros avanzados" onClick={toggleAdvanced}>
                                <FaSlidersH />
                            </button>
                        </div>
                        <AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced} className="LStudio__sticky_advanced">
                            <FieldSelect id="sticky_filter_pinned" label="Fijada" value={advanced.pinned} onChange={(event) => setAdvanced((current) => ({ ...current, pinned: event.target.value }))} options={[
                                { value: "ALL", label: "Todas" }, { value: "YES", label: "Fijadas" }, { value: "NO", label: "No fijadas" },
                            ]} />
                            <FieldSelect id="sticky_filter_color" label="Color" value={advanced.color} onChange={(event) => setAdvanced((current) => ({ ...current, color: event.target.value }))} options={[
                                { value: "ALL", label: "Todos" }, ...COLORS,
                            ]} />
                            <FieldSelect id="sticky_filter_date_field" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[
                                { value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" },
                            ]} />
                            <FieldDate id="sticky_filter_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(value) => setAdvanced((current) => ({ ...current, dateRange: [value, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                            <FieldDate id="sticky_filter_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(value) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, value], dateExact: null }))} isClearable />
                            <FieldDate id="sticky_filter_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(value) => setAdvanced((current) => ({ ...current, dateExact: value, dateRange: [null, null] }))} isClearable />
                        </AdvancedFilterPanel>
                        <div className="LStudio__sticky_list_meta"><span>{filtered.length} {filtered.length === 1 ? "nota" : "notas"}</span></div>
                        <div className="LStudio__sticky_list">
                            {Q.useApiLoading && <Skeleton variant="box" height={98} count={4} />}
                            {!Q.useApiLoading && filtered.map((note) => {
                                const isActive = String(note.id) === String(selectedId);
                                return <button
                                    key={note.id} type="button" className="LStudio__sticky_note_item"
                                    data-active={isActive} data-color={note.color || "CHARCOAL"}
                                    title={isActive ? "Ocultar nota" : "Abrir nota"}
                                    onClick={() => isActive ? deselectNote() : selectNote(note)}
                                >
                                    <span className="LStudio__sticky_note_item_head"><strong>{noteLabel(note)}</strong>{note.isPinned && <FaThumbtack title="Fijada" />}</span>
                                    <span className="LStudio__sticky_note_item_preview">{notePreview(note)}</span>
                                    <small>{onFormatUpdated(note.updatedAt || note.createdAt)}</small>
                                </button>;
                            })}
                            {!Q.useApiLoading && !filtered.length && notes.length > 0 && <div className="LStudio__sticky_list_empty">No hay coincidencias.</div>}
                        </div>
                    </aside>

                    <section className="LStudio__sticky_editor_panel">
                        {Q.useApiLoading && <Skeleton variant="box" height={500} />}
                        {!Q.useApiLoading && !selected && notes.length === 0 && <EmptyState icon={<FaStickyNote />} title="Todavía no hay notas rápidas"
                            description="Creá una nota, escribí y seguí con lo tuyo. NOTEBOOK la guarda automáticamente."
                            action={<AppButton icon={<FaPlus />} onClick={createNote} isLoading={creating}>Crear primera nota</AppButton>} />}
                        {!Q.useApiLoading && !selected && notes.length > 0 && <EmptyState icon={<FaStickyNote />} title="Ninguna nota abierta"
                            description="Seleccioná una nota de la izquierda cuando quieras verla o editarla. Podés dejar este espacio vacío."
                            action={<AppButton icon={<FaPlus />} onClick={createNote} isLoading={creating}>Nueva nota</AppButton>} />}

                        {!Q.useApiLoading && selected && <div className="LStudio__sticky_editor" data-color={draft.color || "CHARCOAL"}>
                            <div className="LStudio__sticky_editor_top">
                                <div className="LStudio__sticky_save_state" data-state={saveState}>{status.icon}<span>{status.text}</span><small>Ctrl+S</small></div>
                                <div className="LStudio__sticky_editor_actions">
                                    <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaEyeSlash />} onClick={deselectNote} title="Dejar la nota sin mostrar">Ocultar</AppButton>
                                    <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaSave />} onClick={() => saveCurrent({ force: true })}>Guardar ahora</AppButton>
                                    <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaThumbtack />} onClick={togglePin}>{selected.isPinned ? "Desfijar" : "Fijar"}</AppButton>
                                    <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />} onClick={archiveSelected}>Archivar</AppButton>
                                </div>
                            </div>

                            <input className="LStudio__sticky_title_input" value={draft.title}
                                onChange={(event) => queueSave({ ...draftRef.current, title: event.target.value })}
                                placeholder="Título opcional" maxLength={180} />

                            <div className="LStudio__sticky_color_row" aria-label="Color de la nota">
                                <span>Color</span>
                                {COLORS.map((color) => <button key={color.value} type="button" title={color.label}
                                    className="LStudio__sticky_color" data-color={color.value} data-active={draft.color === color.value}
                                    onClick={() => queueSave({ ...draftRef.current, color: color.value })}><span /></button>)}
                            </div>

                            <div className="LStudio__sticky_richtext">
                                <FieldRichText id={`sticky_note_${selected.id}`} label={null} hint="" templateId="tiptap" minHeight={420}
                                    enableImageControls
                                    value={draft.content}
                                    onChange={(value) => queueSave({ ...draftRef.current, content: value })}
                                    placeholder="Escribí algo rápido… Podés usar negrita, cursiva, viñetas, listas numeradas, enlaces y más." />
                            </div>
                            <div className="LStudio__sticky_editor_footer">
                                <span>{plain(draft.content).length.toLocaleString("es-AR")} caracteres</span>
                                <span>Última actualización: {onFormatUpdated(selected.updatedAt || selected.createdAt) || "—"}</span>
                            </div>
                        </div>}
                    </section>
                </div>
            </section>
        </div>
    );
};
